(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const selectors = {
    card: '[data-askr-quick-view-card]',
    data: '[data-askr-quick-view-data]',
  };

  let root = null;
  let activeTrigger = null;
  let activeProduct = null;
  let closingTimer = null;

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const getProductData = (element) => {
    const component = element.closest('product-component');
    const script = component?.querySelector(selectors.data);
    if (!script?.textContent) return null;

    try {
      const data = JSON.parse(script.textContent);
      if (!data?.title || !data?.url) return null;
      return data;
    } catch (error) {
      console.error('ASKR quick view data could not be parsed.', error);
      return null;
    }
  };

  const getCartAddUrl = () => {
    const configuredUrl = window.routes?.cart_add_url || '/cart/add';
    const url = new URL(configuredUrl, window.location.origin);

    if (!url.pathname.endsWith('.js')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    }

    return `${url.pathname}${url.search}`;
  };

  const ensureRoot = () => {
    if (root) return root;

    root = document.createElement('div');
    root.className = 'askr-qv';
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = `
      <div class="askr-qv__overlay" data-askr-qv-close></div>
      <section class="askr-qv__panel" role="dialog" aria-modal="true" aria-labelledby="askr-qv-title" tabindex="-1">
        <button class="askr-qv__close" type="button" aria-label="Close product preview" data-askr-qv-close>
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M5.2 5.2 14.8 14.8M14.8 5.2 5.2 14.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="askr-qv__content" data-askr-qv-content></div>
      </section>
    `;
    document.body.appendChild(root);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-askr-qv-close]')) closeQuickView();

      const thumb = event.target.closest('[data-askr-qv-thumb]');
      if (thumb) setActiveImage(Number(thumb.dataset.askrQvThumb || 0));

      const stepper = event.target.closest('[data-askr-qv-step]');
      if (stepper) stepQuantity(Number(stepper.dataset.askrQvStep || 0));

      const addButton = event.target.closest('[data-askr-qv-add]');
      if (addButton) addToCart(addButton);
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeQuickView();
      if (event.key === 'Tab') keepFocusInside(event);
    });

    return root;
  };

  const renderMedia = (product) => {
    const media = product.media?.length ? product.media : [];
    const main = media[0];
    if (!main) {
      return '<div class="askr-qv__empty-media">Image preview unavailable</div>';
    }

    const thumbs = media
      .map(
        (item, index) => `
          <button class="askr-qv__thumb${index === 0 ? ' is-active' : ''}" type="button" data-askr-qv-thumb="${index}" aria-label="View image ${index + 1}">
            <img src="${escapeHtml(item.thumb || item.src)}" alt="">
          </button>
        `,
      )
      .join('');

    return `
      <div class="askr-qv__media">
        <div class="askr-qv__main-media">
          <img data-askr-qv-main-image src="${escapeHtml(main.src)}" alt="${escapeHtml(main.alt || product.title)}">
        </div>
        ${media.length > 1 ? `<div class="askr-qv__thumbs" aria-label="Product images">${thumbs}</div>` : ''}
      </div>
    `;
  };

  const renderProduct = (product) => {
    const canAdd = product.available && product.variantId && Number(product.priceRaw) > 0 && product.hasOnlyDefaultVariant;
    const price = Number(product.priceRaw) > 0 ? product.price : 'Pricing in preparation';
    const description =
      product.description ||
      'A dark ceramic object from the ASKR Wares first archive, selected for surface, silhouette, and room presence.';

    return `
      <div class="askr-qv__grid">
        ${renderMedia(product)}
        <div class="askr-qv__info">
          <p class="askr-qv__eyebrow">${escapeHtml(product.vendor || 'ASKR Wares')}</p>
          <h2 id="askr-qv-title">${escapeHtml(product.title)}</h2>
          <div class="askr-qv__price">${escapeHtml(price)}</div>
          <div class="askr-qv__notes" aria-label="Object notes">
            <span>${escapeHtml(product.note || 'Small-batch object')}</span>
            <span>${escapeHtml(product.usageNote || 'Display-first decorative use')}</span>
            <span>Ships protected for fragile ceramic</span>
          </div>
          <p class="askr-qv__description">${escapeHtml(description)}</p>
          <div class="askr-qv__actions">
            ${
              canAdd
                ? `
                  <div class="askr-qv__quantity" aria-label="Quantity">
                    <button type="button" data-askr-qv-step="-1" aria-label="Decrease quantity">-</button>
                    <input data-askr-qv-quantity type="number" min="1" step="1" value="1" inputmode="numeric" aria-label="Quantity">
                    <button type="button" data-askr-qv-step="1" aria-label="Increase quantity">+</button>
                  </div>
                  <button class="askr-qv__add" type="button" data-askr-qv-add>Add to cart</button>
                `
                : `<a class="askr-qv__add askr-qv__add--link" href="${escapeHtml(product.url)}">View full details</a>`
            }
          </div>
          <a class="askr-qv__details" href="${escapeHtml(product.url)}">Open full product page</a>
          <p class="askr-qv__status" data-askr-qv-status role="status" aria-live="polite"></p>
        </div>
      </div>
    `;
  };

  const setActiveImage = (index) => {
    if (!activeProduct?.media?.[index]) return;

    const image = root.querySelector('[data-askr-qv-main-image]');
    const thumbs = [...root.querySelectorAll('[data-askr-qv-thumb]')];
    const media = activeProduct.media[index];

    image.src = media.src;
    image.alt = media.alt || activeProduct.title || '';
    thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));
  };

  const stepQuantity = (step) => {
    const input = root.querySelector('[data-askr-qv-quantity]');
    if (!input) return;

    const nextValue = Math.max(1, (parseInt(input.value, 10) || 1) + step);
    input.value = String(nextValue);
  };

  const setStatus = (message, isError = false) => {
    const status = root?.querySelector('[data-askr-qv-status]');
    if (!status) return;

    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const addToCart = async (button) => {
    if (!activeProduct?.variantId || button.getAttribute('aria-busy') === 'true') return;

    const quantityInput = root.querySelector('[data-askr-qv-quantity]');
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();

    formData.append('id', activeProduct.variantId);
    formData.append('quantity', String(quantity));

    if (cart?.getSectionsToRender) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding...';
    setStatus('');

    try {
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const responseText = await response.text();
      let parsedState;

      try {
        parsedState = JSON.parse(responseText);
      } catch (error) {
        throw new Error('Cart could not be updated. Please refresh the page and try again.');
      }

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'This object could not be added.');
      }

      button.textContent = 'Added';
      setStatus('Added to cart.');

      if (cart?.renderContents) {
        closeQuickView();
        window.setTimeout(() => cart.renderContents(parsedState), 260);
      }
    } catch (error) {
      button.textContent = 'Add to cart';
      setStatus(error.message || 'Add to cart failed.', true);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const keepFocusInside = (event) => {
    const focusable = [
      ...root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openQuickView = (product, trigger) => {
    const quickView = ensureRoot();
    const content = quickView.querySelector('[data-askr-qv-content]');

    window.clearTimeout(closingTimer);
    activeProduct = product;
    activeTrigger = trigger;
    content.innerHTML = renderProduct(product);

    quickView.classList.remove('is-closing');
    quickView.classList.add('is-open');
    quickView.setAttribute('aria-hidden', 'false');
    document.body.classList.add('askr-qv-open');

    window.requestAnimationFrame(() => {
      quickView.querySelector('.askr-qv__close')?.focus({ preventScroll: true });
    });
  };

  const closeQuickView = () => {
    if (!root?.classList.contains('is-open')) return;

    root.classList.add('is-closing');
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('askr-qv-open');

    closingTimer = window.setTimeout(() => {
      root?.classList.remove('is-closing');
      root?.querySelector('[data-askr-qv-content]')?.replaceChildren();
      activeProduct = null;
      activeTrigger?.focus?.({ preventScroll: true });
      activeTrigger = null;
    }, 320);
  };

  const isPlainPrimaryClick = (event) => {
    if (!desktopQuery.matches) return false;
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    return true;
  };

  const shouldOpenQuickView = (event, link) => {
    if (!isPlainPrimaryClick(event)) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.closest('.quick-add, product-form, modal-opener, quick-add-modal')) return false;
    if (!link.closest(selectors.card)) return false;
    return /\/products\//.test(link.pathname);
  };

  const shouldOpenQuickViewFromCard = (event, card) => {
    if (!card || !isPlainPrimaryClick(event)) return false;
    if (event.target.closest('a[href], button, input, select, textarea, label, summary')) return false;
    if (event.target.closest('.quick-add, product-form, modal-opener, quick-add-modal')) return false;
    return Boolean(card.querySelector('a[href*="/products/"]'));
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');

    if (link && shouldOpenQuickView(event, link)) {
      const product = getProductData(link);
      if (!product) return;

      event.preventDefault();
      openQuickView(product, link);
      return;
    }

    const card = event.target.closest(selectors.card);
    if (!shouldOpenQuickViewFromCard(event, card)) return;

    const product = getProductData(card);
    if (!product) return;

    event.preventDefault();
    openQuickView(product, card);
  });
})();
